import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { DisclaimerTypes } from '../../../interfaces/disclaimertypes';
import { GenericStreamListForUserSelection, GenericStreamTag, GenericUserStream, StreamOrderItem } from '../../../interfaces/responsedata.ts/generic-streams';
import { Userdata } from '../../../interfaces/userdata';
import { GetCategoryNameByIdPipe } from '../../../pipes/get-category-name-by-id.pipe';
import { AppdataService } from '../../../service/appdata.service';
import { GenericStreamService } from '../../../service/generic-stream.service';
import { SnackService } from '../../../service/snack.service';
import { UserdataService } from './../../../service/userdata.service';
import { NameStreamComponent } from './name-stream/name-stream.component';
import { SelectStreamComponent } from './select-stream/select-stream.component';

// those are hard-coded ids for the followign IDS streams:
// Landing Page - Pitch Deck and LinkedIN

@Component({
    selector: 'app-generic-stream-dashboard',
    standalone: false,
    templateUrl: './generic-stream-dashboard.component.html',
    styleUrl: './generic-stream-dashboard.component.scss',
    providers: [GetCategoryNameByIdPipe]
})
export class GenericStreamDashboardComponent implements OnInit {
    user = inject(UserdataService);
    dialog = inject(MatDialog);
    route = inject(ActivatedRoute);
    appData = inject(AppdataService);
    cd = inject(ChangeDetectorRef);
    genService = inject(GenericStreamService);
    router = inject(Router);
    snack = inject(SnackService);
    destroyRef = inject(DestroyRef);
    categoryNameByIdPipe = inject(GetCategoryNameByIdPipe);

    // Variables
    userData: Userdata | undefined;
    today = new Date().toLocaleDateString()
    yesterday = new Date(new Date().setDate(new Date().getDate() - 1)).toLocaleDateString();
    isLoading: boolean = false;
    disclaimerType: DisclaimerTypes = DisclaimerTypes.PAGE_FOOTER;

    // Objects
    emptyStateElement: any[] = []
    userStreams: GenericUserStream[] = [];
    genstreams: GenericStreamListForUserSelection[] = [];
    genStreamNamesById: any = [];
    groupedStreams: { date: string, streams: GenericUserStream[] }[] = [];


    ngOnInit(): void {
        this.user.data.subscribe(async (data) => {
            if (data) {
                this.userData = data;
                if(this.userData.userGateConfig && !this.userData.userGateProgress) {
                    let result = await this.genService.updateUserGateProgress('INIT');
                    if (result !== false) {
                        this.userData.userGateProgress = result;
                        this.user.updateUserData(this.userData)
                        console.log('Progress updated', result)
                    }
                }
            }
        });

        this.loadStreams();
    }

    async loadStreams() {
        this.genstreams = [];
        this.userStreams = [];
        this.groupedStreams = [];
        this.isLoading = true;
        let [genstreams, streamNames] = await Promise.all([
            this.genService.getGenericStreams(),
            this.genService.getGenericStreamNames()
        ]);

        this.isLoading = false;
        if (genstreams === false) {
            return;
        }

        const tree = await this.appData.loadStructuredCategoriesAndStreams();
        if (tree !== false) {
            this.getStreamCards(genstreams, tree);
        }

        this.genstreams = genstreams;
        this.genStreamNamesById = streamNames;



        let s = await this.genService.getGenericUserStreams();
        if (s === false) return;
        this.userStreams = s;

        this.groupDataForView();

    }

    findCategorysByConfigId(id: string): string[] {
        let c = this.genstreams.find(stream => stream.id === id);
        return c ? c.categoryId : [];
    }

    findStreamNameByConfigId(id: string): string | null {
        const stream = this.genStreamNamesById.find((stream: GenericStreamTag) => stream.id === id);

        return stream ? stream.name : "Stream deprecated";
    }

    async groupDataForView() {
        for await (let stream of this.userStreams) {
            let date = stream.dateUpdate || stream.dateCreate;
            let day = new Date(date!).toLocaleDateString()

            let exists = this.groupedStreams.find((s: any) => s.date == day);
            if (!exists) {
                this.groupedStreams.push({
                    date: day,
                    streams: [stream]
                })
            } else {
                this.groupedStreams.forEach((t) => {
                    if (t.date == day) {
                        t.streams.push(stream);
                    }
                })
            }
        }
    }
    async deleteStream(id: string) {
        let c = confirm("Are you sure you want to delete this stream and all generated data?")
        if (!c) return;
        this.isLoading = true;
        let result = await this.genService.deleteGenericUserStream(id);
        this.isLoading = false;
        if (result === false) return;
        this.loadStreams();

    }
    async newStream() {

        let dia = this.dialog.open(SelectStreamComponent, {
            panelClass: 'small-dialog-width',
            autoFocus: false,
            data: {
                userCredits: this.userData?.licenseData?.[0]?.credits,
                streamList: this.genstreams,
                userGateConfig: this.userData?.userGateConfig,
                userGateProgress: this.userData?.userGateProgress,
            }
        });

        dia.afterClosed().subscribe(result => {
            if (!result) return;
            let selectedStream = this.genstreams.find(s => s.id === result);
            if (!selectedStream) return;
            let diaNaming = this.dialog.open(NameStreamComponent, {
                panelClass: 'small-dialog',
                autoFocus: false,
                data: { name: selectedStream?.name }
            });
            diaNaming.afterClosed().subscribe(async resultName => {
                if (!resultName) return;
                if (result && typeof result === 'string') {
                    this.isLoading = true;
                    let newStreamId = await this.genService.createGenericUserStream(result, resultName);
                    this.isLoading = false;
                    if (newStreamId === false) return;

                    this.router.navigate(['/generic-stream', newStreamId], { queryParams: { cid: selectedStream?.id } });
                }
            });
        });
    }

    protected async onStreamCardClick(
        streamId: string | undefined,
    ): Promise<void> {
        if (!streamId) {
            return;
        }

        const stream = this.genstreams.find(s => s.id === streamId);

        // check if stream exists
        if (!stream) {
            this.snack.error(
                {
                    error: {
                        msg: {
                            headline: 'Error',
                            text: 'Selected stream could not be found.',
                        },
                    },
                });

            return;
        }

        // check if user has enough credits
        const userCredits = this.userData?.licenseData?.[0]?.credits;

        if (
            userCredits === undefined ||
            (
                userCredits !== -1 &&
                stream.credits > userCredits
            )
        ) {
            this.snack.error(
                {
                    error: {
                        msg: {
                            headline: 'Error',
                            text: 'Not enough credits to create this stream. Please add some credits or choose a different stream.',
                        },
                    },
                });

            return;
        }

        const dialog = this.dialog.open(NameStreamComponent, {
            panelClass: 'small-dialog',
            autoFocus: false,
            data: { name: stream.name }
        });

        dialog.afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: async (name) => {
                    if (!name) {
                        return;
                    }

                    if (stream && typeof name === 'string') {
                        this.isLoading = true;

                        const newStreamId = await this.genService.createGenericUserStream(stream.id, name);

                        this.isLoading = false;

                        if (newStreamId === false) {
                            return;
                        };

                        this.router.navigate(['/generic-stream', newStreamId], { queryParams: { cid: stream.id } });
                    }
                }
            });
    }

    private async getStreamCards(genstreams: GenericStreamListForUserSelection[], treeElement: StreamOrderItem[]) {
        this.emptyStateElement = []
        for (const element of treeElement) {
            if (this.emptyStateElement.length === 3) return
            const stream = genstreams.find(s => s.id === element.streamId)
            if (!stream) continue;
            const catName = this.categoryNameByIdPipe.transform(stream?.categoryId?.[0] ?? '')

            this.emptyStateElement.push({
                title: stream?.name,
                credits: stream?.credits,
                streamId: stream?.id,
                categoryName: catName,
                stepCount: stream?.stepCount
            });
        }

    }
}
